import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, Download, X } from 'lucide-react';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/journal-logo.png';

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const superscripts = ['¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹', '¹⁰'];

const JournalCover = React.forwardRef(({ article, mini = false }, ref) => {
  if (!article) return null;

  const authors = (article.authors || []).slice(0, 10);
  const issueNumber = article.issue_number || 142;
  const dateStr = article.date_str || '';

  const padding = mini ? 'px-2 py-2' : 'px-6 sm:px-10 py-6 sm:py-8';

  return (
    <div
      ref={ref}
      data-testid={mini ? 'journal-cover-mini' : 'journal-cover'}
      className={mini ? 'shadow' : 'shadow-lg'}
      style={{
        fontFamily: "'Times New Roman', 'Georgia', serif",
        background: 'linear-gradient(135deg, #f0ede6 0%, #e8e5dd 50%, #f0ede6 100%)',
        color: '#1a1a1a',
        width: mini ? '100%' : '600px'
      }}
    >
      <div className={padding}>
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div style={{ fontVariant: 'small-caps', letterSpacing: '0.05em', fontSize: mini ? '0.4rem' : 'clamp(0.7rem, 1.5vw, 0.85rem)' }}>
            <span className="font-bold">Vol.</span> 148 | <span className="font-bold">Issue</span> {issueNumber}
          </div>
          <div className="italic" style={{ fontSize: mini ? '0.4rem' : 'clamp(0.7rem, 1.5vw, 0.85rem)' }}>
            {dateStr}
          </div>
        </div>

        {/* Logo */}
        <div className="flex justify-center" style={{ margin: mini ? '4px 0' : '12px 0' }}>
          <img src={LOGO_URL} alt="Journal Seal" className="object-cover rounded-full"
            crossOrigin="anonymous"
            style={{ width: mini ? '24px' : '96px', height: mini ? '24px' : '96px' }} />
        </div>

        {/* Title */}
        <div className="text-center" style={{ marginBottom: mini ? '4px' : '4px' }}>
          <h2 style={{ fontSize: mini ? '0.5rem' : 'clamp(1.4rem, 4.5vw, 2.4rem)', fontWeight: '400', lineHeight: '1.15' }}>
            <span className="font-bold" style={{ fontVariant: 'small-caps', letterSpacing: '0.08em' }}>The Journal </span>
            <span className="italic font-normal" style={{ fontSize: '0.7em', margin: '0 0.15em' }}>of</span>
            <span className="font-bold" style={{ fontVariant: 'small-caps', letterSpacing: '0.08em' }}> Residency Research</span>
          </h2>
          {!mini && (
            <p className="text-[9px] sm:text-[11px] tracking-[0.25em] italic mt-0.5" style={{ color: '#444' }}>
              For Clinical Residents and Beyond
            </p>
          )}
        </div>

        {/* Divider */}
        <div style={{ margin: mini ? '4px 0' : '12px 0' }}>
          <hr className="border-t-2 border-gray-700 mb-0.5" />
          {!mini && <hr className="border-t border-gray-500" />}
        </div>

        {/* Article Title */}
        <div className="text-center" style={{ marginBottom: mini ? '4px' : '12px', padding: mini ? '0' : '0 8px' }}>
          <h3 className="font-bold" style={{ fontSize: mini ? '0.4rem' : 'clamp(1.1rem, 3.2vw, 1.55rem)', lineHeight: '1.35' }}>
            {article.tema}
          </h3>
        </div>

        {!mini && (
          <>
            <hr className="border-gray-400 mb-3" />

            {/* Authors inline */}
            {authors.length > 0 && (
              <>
                <div className="mb-2 text-center" style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.8rem)', lineHeight: '1.8' }}>
                  {authors.map((a, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      <span className="font-bold">{a.full_name}</span>
                      <sup className="text-[0.7em]">{superscripts[i]}</sup>
                    </span>
                  ))}
                </div>
                <div className="mb-4 text-center" style={{ fontSize: 'clamp(0.55rem, 1.4vw, 0.68rem)', lineHeight: '1.7', color: '#444' }}>
                  {authors.map((a, i) => (
                    <span key={i}>
                      {i > 0 && '  '}
                      <sup className="text-[0.8em]">{superscripts[i]}</sup>{a.universidad || 'Independent Researcher'}
                    </span>
                  ))}
                </div>
              </>
            )}

            {/* Abstract */}
            <div style={{ fontSize: 'clamp(0.6rem, 1.5vw, 0.75rem)', lineHeight: '1.55' }}>
              <h4 className="text-center font-bold tracking-[0.15em] mb-2" style={{ fontSize: 'clamp(0.85rem, 2.2vw, 1.05rem)' }}>
                ABSTRACT
              </h4>
              <p className="mb-2"><strong>Antecedentes:</strong> {article.antecedentes}</p>
              <p className="mb-2"><strong>Métodos:</strong> {article.metodos}</p>
              <p className="mb-2"><strong>Resultados:</strong> {article.resultados}</p>
              <p><strong>Conclusiones:</strong> {article.conclusiones}</p>
            </div>

            {/* Footer */}
            <hr className="border-gray-500 mt-5 mb-2" />
            <p className="text-center italic" style={{ fontSize: 'clamp(0.55rem, 1.3vw, 0.7rem)', color: '#444', lineHeight: '1.4' }}>
              Este artículo forma parte de la serie diaria de investigación del ranking donde los 10
              residentes con mayor puntuación del día participan como autores del estudio.
              Este estudio no es una investigación real, es un ejercicio ficticio con fines de entretenimiento.
            </p>
          </>
        )}
      </div>
    </div>
  );
});

const JOURNAL_WIDTH = 600;

const MobileJournalWrapper = ({ article }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [journalHeight, setJournalHeight] = useState(0);
  const innerRef = useRef(null);

  useEffect(() => {
    const calculate = () => {
      if (containerRef.current) {
        setScale(Math.min(1, containerRef.current.offsetWidth / JOURNAL_WIDTH));
      }
      if (innerRef.current) {
        setJournalHeight(innerRef.current.offsetHeight);
      }
    };
    calculate();
    const timer = setTimeout(calculate, 300);
    window.addEventListener('resize', calculate);
    return () => { clearTimeout(timer); window.removeEventListener('resize', calculate); };
  }, [article]);

  return (
    <div className="sm:hidden" ref={containerRef}>
      <div style={{ height: journalHeight * scale * 1.12, overflow: 'hidden' }}>
        <div ref={innerRef} style={{ width: `${JOURNAL_WIDTH}px`, transform: `scale(${scale})`, transformOrigin: 'top left', paddingBottom: '40px' }}>
          <JournalCover article={article} />
        </div>
      </div>
    </div>
  );
};

const JournalDelDia = ({ user, onLogout }) => {
  const [currentArticle, setCurrentArticle] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [downloadingSelected, setDownloadingSelected] = useState(false);
  const journalRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    fetchData();
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport?.getAttribute('content');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover');
    }
    return () => {
      if (viewport && originalContent) viewport.setAttribute('content', originalContent);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [todayRes, historyRes] = await Promise.all([
        apiClient.get('/journal/today'),
        apiClient.get('/journal/history')
      ]);
      setCurrentArticle(todayRes.data);
      setHistory(historyRes.data || []);
    } catch (error) {
      toast.error('Error al cargar el journal');
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async (element) => {
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#f0ede6',
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 5000,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });
      return new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png');
      });
    } catch (err) {
      console.error('html2canvas error:', err);
      return null;
    }
  };

  const getOffscreenTarget = () => {
    return document.getElementById('journal-offscreen-render');
  };

  const getSelectedOffscreenTarget = () => {
    return document.getElementById('journal-selected-offscreen-render');
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const target = getOffscreenTarget();
      if (!target) { toast.error('No se encontró el journal'); setSharing(false); return; }

      await new Promise(r => setTimeout(r, 200));
      const blob = await generateImage(target);
      if (!blob) { toast.error('Error generando imagen'); setSharing(false); return; }

      // Try native share on mobile
      if (navigator.share) {
        try {
          const file = new File([blob], 'journal-residency-research.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'The Journal of Residency Research',
              text: `Artículo del día: ${currentArticle?.tema || ''}`,
              files: [file]
            });
            setSharing(false);
            return;
          }
        } catch (shareErr) {
          if (shareErr.name !== 'AbortError') {
            console.error('Share API failed, falling back to download:', shareErr);
          } else {
            setSharing(false);
            return;
          }
        }
      }
      // Fallback: download
      downloadBlob(blob, 'journal-residency-research.png');
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Error al compartir');
    }
    setSharing(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const target = getOffscreenTarget();
      if (!target) { toast.error('No se encontró el journal'); setDownloading(false); return; }

      await new Promise(r => setTimeout(r, 200));
      const blob = await generateImage(target);
      if (!blob) { toast.error('Error generando imagen'); setDownloading(false); return; }
      downloadBlob(blob, `journal-issue-${currentArticle?.issue_number || 'current'}.png`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error al descargar');
    }
    setDownloading(false);
  };

  const handleDownloadSelected = async () => {
    setDownloadingSelected(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      const target = getSelectedOffscreenTarget();
      if (!target) { toast.error('No se encontró el journal'); setDownloadingSelected(false); return; }
      const blob = await generateImage(target);
      if (!blob) { toast.error('Error generando imagen'); setDownloadingSelected(false); return; }
      downloadBlob(blob, `journal-issue-${selectedArticle?.issue_number || 'prev'}.png`);
    } catch (error) {
      console.error('Download selected error:', error);
      toast.error('Error al descargar');
    }
    setDownloadingSelected(false);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 2000);
    toast.success('Imagen descargada');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" data-testid="back-to-dashboard" className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Menú Principal</span>
          </Link>
          <button onClick={onLogout} className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all">
            <span className="text-black text-sm px-2">Salir</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        {/* Title */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-1">
            <img src={LOGO_URL} alt="Journal" className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded-full" />
          </div>
          <h1 data-testid="journal-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black" style={{ fontFamily: "'Times New Roman', 'Georgia', serif" }}>
            Journal del Día
          </h1>
          <p className="text-xs text-gray-400 mt-1">Publicación diaria a las 9:00 PM</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-2 max-w-md mx-auto leading-relaxed">
            Los 10 médicos con mayor puntaje en el Ranking del Día son seleccionados como autores del artículo publicado cada noche.
          </p>
        </div>

        {!currentArticle ? (
          <div className="text-center py-12" data-testid="journal-empty">
            <h3 className="text-xl font-semibold text-black mb-2">Sin publicación</h3>
            <p className="text-gray-600">Aún no hay un journal publicado.</p>
          </div>
        ) : (
          <>
            {/* Buttons: Compartir + Descargar */}
            <div className="flex justify-center gap-3 mb-6">
              <button
                onClick={handleShare}
                disabled={sharing}
                data-testid="journal-share-button"
                className="inline-flex items-center gap-2 px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                <Share2 size={16} />
                {sharing ? 'Generando...' : 'Compartir'}
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="journal-download-button"
                className="inline-flex items-center gap-2 px-5 py-2 bg-white text-black border border-black rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                <Download size={16} />
                {downloading ? 'Descargando...' : 'Descargar Imagen'}
              </button>
            </div>

            {/* Current Journal - Desktop (centered) */}
            <div className="hidden sm:flex justify-center mb-8">
              <JournalCover ref={journalRef} article={currentArticle} />
            </div>

            {/* Current Journal - Mobile (scaled, centered) */}
            <div className="mb-8">
              <MobileJournalWrapper article={currentArticle} />
            </div>

            {/* History section */}
            {history.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg font-bold text-black mb-4">Ediciones anteriores</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {history.map((article, i) => (
                    <div
                      key={i}
                      onClick={() => setSelectedArticle(article)}
                      className="border border-gray-200 rounded overflow-hidden hover:border-black transition-colors cursor-pointer"
                      data-testid={`journal-history-${i}`}
                    >
                      <JournalCover article={article} mini />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-8 text-center">
          <Link to="/" data-testid="back-menu-button" className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            Menú Principal
          </Link>
        </div>
      </div>

      {/* Modal for selected previous journal */}
      {selectedArticle && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center overflow-y-auto py-8 px-4" onClick={() => setSelectedArticle(null)}>
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedArticle(null)}
              data-testid="close-journal-modal"
              className="absolute -top-3 -right-3 z-10 w-8 h-8 bg-white border border-black rounded-full flex items-center justify-center hover:bg-gray-100"
            >
              <X size={16} />
            </button>
            <div className="flex justify-center mb-3">
              <button
                onClick={handleDownloadSelected}
                disabled={downloadingSelected}
                data-testid="download-selected-journal"
                className="inline-flex items-center gap-2 px-5 py-2 bg-white text-black border border-black rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                <Download size={16} />
                {downloadingSelected ? 'Descargando...' : 'Descargar Imagen'}
              </button>
            </div>
            <JournalCover article={selectedArticle} />
          </div>
        </div>
      )}

      {/* Offscreen renders for html2canvas (always visible, positioned off-screen) */}
      {currentArticle && (
        <div style={{ position: 'fixed', left: '-9999px', top: '0', zIndex: -1 }}>
          <div id="journal-offscreen-render">
            <JournalCover article={currentArticle} />
          </div>
        </div>
      )}
      {selectedArticle && (
        <div style={{ position: 'fixed', left: '-9999px', top: '0', zIndex: -1 }}>
          <div id="journal-selected-offscreen-render">
            <JournalCover article={selectedArticle} />
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalDelDia;
